import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight, UserCheck, Calendar } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo, StructuredData } from "@/lib/seo";
import { CLUSTER_LABELS } from "@/lib/constants";
import NotFound from "@/pages/marketing/NotFound";

function ContinueLearning({ cl }) {
  const to = cl.catalog ? "/courses" : `/courses/${cl.slug}`;
  return (
    <div className="mt-14 border-l-2 border-[#C2836B] pl-6" data-testid="article-continue-learning">
      <div className="eyebrow text-[#C2836B] mb-2">Continue Learning With Andrea</div>
      {cl.catalog ? (
        <p className="text-[#A39E98] text-[15px] leading-relaxed">If you're weighing up a course, you can compare all five in the course catalog, from Foundations of Infant Sleep to naps, night wakings, regressions and bedtime, in both Independent and Faculty-Led formats.</p>
      ) : (
        <p className="text-[#A39E98] text-[15px] leading-relaxed"><span className="text-[#F7F5F0]">{cl.code} {cl.title}</span> covers this topic in Unit {cl.unit_number}, {cl.unit}. The course goes further into {cl.focus}, with structured lessons, readings and coursework.</p>
      )}
      <Link to={to} data-testid="article-continue-btn" className="btn-outline-night px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 mt-4">{cl.catalog ? "Explore the Course Catalog" : "Explore This Course"} <ArrowRight size={15} /></Link>
    </div>
  );
}


export default function Article() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [pg, setPg] = useState(undefined);

  useEffect(() => {
    setPg(undefined);
    http.get(`/content/${slug}`).then((r) => setPg(r.data)).catch(() => setPg(null));
  }, [slug]);

  useSeo({
    title: pg?.title || "Infant Sleep Help",
    description: pg?.meta,
    canonical: pg ? `/${pg.slug}` : `/${slug}`,
    noindex: pg === null || pg?.index_status === "noindex",
  });
  if (pg === null) return <NotFound />;

  if (pg === undefined) return <MarketingShell><div className="max-w-3xl mx-auto px-5 py-32 text-[#A39E98]">Loading…</div></MarketingShell>;

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—";

  return (
    <MarketingShell>
      <StructuredData data={{ "@context": "https://schema.org", "@type": "Article", headline: pg.h1,
        author: { "@type": "Person", name: pg.author?.name }, datePublished: pg.created_at, dateModified: pg.updated_at }} />
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-14 pb-8">
        <nav className="flex items-center gap-1.5 text-xs text-[#738075] mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#A39E98]">Infant Sleep Consultant Near Me</Link><ChevronRight size={13} />
          <Link to="/resources" className="hover:text-[#A39E98]">Infant Sleep Library</Link><ChevronRight size={13} />
          <span className="text-[#A39E98]">{CLUSTER_LABELS[pg.cluster] || pg.cluster_label}</span>
        </nav>
        <div className="eyebrow text-[#C2836B] mb-4">{CLUSTER_LABELS[pg.cluster] || pg.cluster_label}</div>
        <h1 className="font-display text-4xl sm:text-5xl text-[#F7F5F0] tracking-tight leading-[1.1]">{pg.h1}</h1>
        {pg.course && (
          <p className="mt-4 text-sm text-[#A39E98]" data-testid="article-course-link">
            <span className="text-[#738075]">{pg.course.label} · </span>
            <Link to={`/courses/${pg.course.slug}`} className="text-[#C2836B] underline underline-offset-4 hover:text-[#F7F5F0]">{pg.course.code} {pg.course.title}</Link>
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-7 pb-7 border-b border-[#2E2C2A] text-sm text-[#738075]">
          <span className="flex items-center gap-2"><UserCheck size={15} /> <span>By <Link to="/#instructor" data-testid="article-author-link" className="underline-offset-4 hover:underline hover:text-[#F7F5F0]">{pg.author?.name}</Link>, {pg.author?.role}</span></span>
          {pg.content_status === "Draft" && <span className="text-[#E4D3AC]" data-testid="article-draft-notice">Draft, awaiting Andrea's review</span>}
          <span className="flex items-center gap-2"><Calendar size={15} /> Updated {fmt(pg.updated_at)}</span>
        </div>

        <div className="mt-10 space-y-10">
          {(pg.body || []).map((sec, i) => (
            <section key={i}>
              <h2 className="font-display text-2xl text-[#F7F5F0] mb-4">{sec.heading}</h2>
              <div className="space-y-4">
                {sec.paragraphs.map((p, j) => <p key={j} className="text-[#A39E98] text-[17px] leading-[1.8]">{p}</p>)}
                {sec.bullets?.length > 0 && <ul className="list-disc pl-6 space-y-2 text-[#A39E98] text-[17px] leading-[1.7]">{sec.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}
              </div>
            </section>
          ))}
        </div>

        {pg.continue_learning && <ContinueLearning cl={pg.continue_learning} />}

        {/* Academic CTA on every article; consultant-search pages add a clearly separate optional consulting link */}
        <div className="card-night rounded-xl p-7 mt-14" data-testid="article-academic-cta">
          <div className="eyebrow text-[#C2836B] mb-2">Your next step, if you want one</div>
          <h3 className="font-display text-2xl text-[#F7F5F0]">Need help with your child's sleep?</h3>
          <p className="text-[#A39E98] text-[15px] mt-2">Every family's situation is different. If you'd like to talk through what's happening with your child, you can explore personal guidance with Andrea. If you'd rather learn independently, our sleep courses give you a structured place to begin.{pg.topic_course && !pg.continue_learning ? <> The course closest to this topic is <Link to={`/courses/${pg.topic_course.slug}`} data-testid="article-topic-course" className="text-[#C2836B] underline underline-offset-4 hover:text-[#F7F5F0]">{pg.topic_course.code} {pg.topic_course.title}</Link>.</> : null}</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to="/pricing" data-testid="article-cta" className="btn-clay px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">Work With Andrea <ArrowRight size={15} /></Link>
            <Link to="/courses" data-testid="article-catalog" className="btn-outline-night px-5 py-2.5 rounded-full text-sm">Explore Sleep Courses</Link>
          </div>
          <button data-testid="article-enroll-free" onClick={() => nav(user?.role === "customer" ? "/portal/progress" : "/register?next=/portal/progress")} className="text-[#738075] text-xs mt-5 underline underline-offset-4 hover:text-[#A39E98]">Prefer to start by tracking your baby's sleep? Use the Free Sleep Tracker.</button>
        </div>
      </article>

      {pg.related?.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
          <div className="eyebrow text-[#738075] mb-4">Related guides</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {pg.related.map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} className="card-night rounded-lg p-4 text-[#F7F5F0] text-sm hover:border-[#C2836B]/50 transition-colors flex items-center justify-between gap-2">
                {r.h1} <ArrowRight size={15} className="text-[#738075] shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </MarketingShell>
  );
}
